import { Box, useMediaQuery, useTheme } from "@mui/material"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import axios from "axios"
import PropTypes from "prop-types"
import queryString from "query-string"
import React, { useContext, useEffect, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"

import { fbEvents } from "../../services/fbConversionsApi"

import LangContext from "../../contexts/LangContext"
import CustomButton from "../buttons/CustomButton"
import CustomTextField from "../form/CustomTextField"
import CustomSelect from "../form/CustomSelect"

const GeneralContact = ({
  formName = "Contact Form",
  location,
  selectedHelperUid = "-",
  selectedTeamMemberUid = "-",
  selectedContactLocationUid = "-",
  showContactLocationSelector = false,
  contactLocations = [],
  showTeamMemberSelector = false,
  teamMembers = [],
  showDateSelector = false,
  spread = false,
  contactMessage,
  disclaimerMessage = "",
  redirectTo,
  sendToCRM = true,
  additionalData = {},
  ...props
}) => {
  const theme = useTheme()
  const lang = useContext(LangContext)
  const i18n = lang.i18n[lang.locale]

  const formNameRef = useRef(null)
  const sourceRef = useRef(null)
  const firstNameRef = useRef(null)
  const lastNameRef = useRef(null)
  const emailRef = useRef(null)
  const phoneRef = useRef(null)
  const messageRef = useRef(null)
  // const newsletterRef = useRef(null)
  const helperRef = useRef(null)
  const teamMemberRef = useRef(null)
  const contactLocationRef = useRef(null)
  const externalLeadIdRef = useRef(null)

  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  // One UUID per lead, shared by the Druid payload (ExternalLeadId) and the
  // form field read by the GHL/Doctrina external tracking script. Generated
  // after mount so the server-rendered markup doesn't pin a value.
  const [externalLeadId, setExternalLeadId] = useState("")

  useEffect(() => {
    setExternalLeadId(uuidv4())
  }, [])
  const [selectedLocation, setSelectedLocation] = useState(selectedContactLocationUid)
  const [filteredTeamMembers, setFilteredTeamMembers] = useState(teamMembers)

  const { className } = props

  if (!disclaimerMessage || disclaimerMessage.length <= 0) {
    disclaimerMessage = i18n.translations.contactDisclaimer
  }

  if (!contactMessage || contactMessage.length <= 0) {
    contactMessage = i18n.translations.contactMessage
  }

  const contactLocationsOptions = contactLocations.map(cl => ({
    label: cl?.data?.title?.text,
    value: cl?.uid,
  }))

  const teamMemberOptions = filteredTeamMembers.map(tm => ({
    label: tm.data.name.text,
    value: tm.uid,
  }))



  const handleLocationChange = event => {
    const selectedValue = event.target.value;

    // Update selected location
    setSelectedLocation(selectedValue);

    // Filter team members based on selected location
    const selectedLocationObj = contactLocations.find(cl => cl.uid === selectedValue);

    if (selectedLocationObj && selectedLocationObj.data.team_members) {
      // Extract team members from the location
      const locationTeamMembers = selectedLocationObj.data.team_members
        .filter(tm => tm.team_member && tm.team_member.document)
        .map(tm => ({
          uid: tm.team_member.document.uid,
          data: {
            name: tm.team_member.document.data.name,
            entitling: tm.team_member.document.data.entitling,
            specialisations: tm.team_member.document.data.specialisations
          }
        }));

      setFilteredTeamMembers(locationTeamMembers);
    } else {
      setFilteredTeamMembers([]);
    }

    // Reset team member selection if one was already selected
    if (teamMemberRef.current) {
      teamMemberRef.current.value = "";
    }
  }

  const sendToDruidAPI = async (data) => {
    const druidUrl = process.env.GATSBY_DRUID_API_URL
    const druidToken = process.env.GATSBY_DRUID_API_TOKEN

    if (!druidUrl || !druidToken) {
      console.warn("Druid API URL or Token not set")
      return
    }

    const payload = {
      targetUserMasterId: "userstart1",
      entityContextData: {
        SiteData: {
          ContactFormSite: data.formName,
          ContactLocation: data.contactLocation,
          Date: new Date().toISOString(),
          Email: data.email,
          ExternalLeadId: data.externalLeadId,
          FirstName: data.firstName,
          Helper: data.helper,
          InfoAresSite: data.message,
          LastName: data.lastName,
          MainPhone: data.phone,
          Medic: data.teamMember,
          Origin: data.source,
        },
      },
      flowUtterance: "_send_client_data_",
      channelId: "directline",
    }

    try {
      const response = await axios.post(druidUrl, payload, {
        headers: {
          Authorization: `Bearer ${druidToken}`,
          "Content-Type": "application/json",
        },
      })
      
      // Log SUCCESS to Netlify Function
      await axios.post("/.netlify/functions/log-druid", {
        status: "success",
        type: "contact-form",
        payload: payload,
        response: response.data
      }).catch(e => console.error("Failed to log success", e))

    } catch (error) {
      console.error("Error sending to Druid API", error)
      
      // Log ERROR to Netlify Function
      await axios.post("/.netlify/functions/log-druid", {
        status: "error",
        type: "contact-form",
        payload: payload,
        response: error.response?.data,
        error: {
          message: error.message,
          status: error.response?.status
        }
      }).catch(e => console.error("Failed to log error", e))
    }
  }

  const handleSubmit = event => {
    event.preventDefault()

    setLoading(true)

    let leadId = externalLeadIdRef.current.value
    if (!leadId) {
      leadId = uuidv4()
      externalLeadIdRef.current.value = leadId
    }

    const formData = {
      "form-name": formNameRef.current.value,
      source: sourceRef.current.value,
      firstName: firstNameRef.current.value,
      lastName: lastNameRef.current.value,
      email: emailRef.current.value,
      phone: phoneRef.current.value,
      helper: helperRef.current.value,
      teamMember: teamMemberRef.current.value,
      contactLocation: contactLocationRef.current.value,
      // newsletter: newsletterRef.current.checked,
      message: messageRef.current.value,
      external_lead_id: leadId,
      ...additionalData,
    }

    // Track form submission event with Facebook Conversions API
    // Pass form name and user data - the Lead event will encode the form name and URL
    fbEvents.lead(
      formNameRef.current.value, // Form name to be encoded
      {
        email: emailRef.current.value,
        phone: phoneRef.current.value,
        firstName: firstNameRef.current.value,
        lastName: lastNameRef.current.value,
      }
    );

    if (sendToCRM) {


      sendToDruidAPI({
        formName: formNameRef.current.value,
        source: sourceRef.current.value,
        firstName: firstNameRef.current.value,
        lastName: lastNameRef.current.value,
        email: emailRef.current.value,
        phone: phoneRef.current.value,
        helper: helperRef.current.value,
        teamMember: teamMemberRef.current.value,
        contactLocation: contactLocationRef.current.value,
        message: messageRef.current.value,
        externalLeadId: leadId,
      })
    }

    const axiosOptions = {
      url: location.pathname,
      method: "post",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      data: queryString.stringify(formData),
    }

    axios(axiosOptions)
      .then(response => {
        //  setSubmitted(true)
        //  setLoading(false)
        //  window.history.pushState("", "", `${location.pathname}multumim/`)
      })
      .catch(err => {
        console.log(err)
        // setSubmitted(false)
        // setLoading(false)
      })

    setSubmitted(true)
    setLoading(false)

    if (redirectTo) {
      window.history.pushState("", "", redirectTo)
    }
  }

  let dense = useMediaQuery(theme.breakpoints.up("sm"))

  if (spread) {
    dense = false
  }

  return (
    <Box
      className={className}
      sx={{
        background: "#f7941d",
        padding: 3,
        height: "100%",
        ...props.sx,
      }}
    >
      {submitted && (
        <Typography
          sx={{
            color: theme => theme.palette.common.white,
            fontSize: 18,
            textAlign: "center",
            paddingTop: 1,
            paddingBottom: 1,
          }}
        >
          {i18n.translations.messageReceivedConfirmation}
        </Typography>
      )}
      {!submitted && (
        <>
          <form
            name={formName}
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={event => handleSubmit(event)}
          >
            <input
              type="hidden"
              name="form-name"
              value={formName}
              ref={formNameRef}
            />

            <input
              type="hidden"
              name="source"
              value={location.href}
              ref={sourceRef}
            />

            <input
              type="hidden"
              name="helper"
              value={selectedHelperUid}
              ref={helperRef}
            />

            <input
              type="hidden"
              name="teamMember"
              value={selectedTeamMemberUid}
              ref={teamMemberRef}
            />

            <input
              type="hidden"
              name="contactLocation"
              value={selectedContactLocationUid}
              ref={contactLocationRef}
            />

            {/* Visually hidden text input instead of type="hidden", because
                the GHL external tracking script ignores hidden inputs. */}
            <input
              type="text"
              name="external_lead_id"
              value={externalLeadId}
              ref={externalLeadIdRef}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              data-ares-external-lead-id="1"
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                opacity: 0,
                pointerEvents: "none",
              }}
            />

            {Object.keys(additionalData).map(key => (
              <input
                type="hidden"
                name={key}
                key={`add_data_${key}`}
                value={additionalData[key]}
              />
            ))}

            <p className="hidden">
              <label htmlFor="bot-field">
                Don’t fill this out if you're human:
                <input id="bot-field" name="bot-field" />
              </label>
            </p>

            <Grid container spacing={dense ? 2 : 0}>
              <Grid item xs={dense ? 3 : 12}>
                <CustomTextField
                  fullWidth
                  type="text"
                  id="firstNameInput"
                  label={i18n.translations.contactFirstName}
                  name="firstName"
                  inputRef={firstNameRef}
                  disabled={loading}
                  required
                />
              </Grid>
              <Grid item xs={dense ? 3 : 12}>
                <CustomTextField
                  fullWidth
                  type="text"
                  id="lastNameInput"
                  label={i18n.translations.contactLastName}
                  name="lastName"
                  inputRef={lastNameRef}
                  disabled={loading}
                  required
                />
              </Grid>
              <Grid item xs={dense ? 3 : 12}>
                <CustomTextField
                  fullWidth
                  type="email"
                  id="emailInput"
                  label={i18n.translations.contactEmail}
                  name="email"
                  inputRef={emailRef}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={dense ? 3 : 12}>
                <CustomTextField
                  fullWidth
                  type="tel"
                  pattern="[0-9\/|+\-\s]+"
                  id="phoneInput"
                  label={i18n.translations.contactPhone}
                  name="phone"
                  inputRef={phoneRef}
                  disabled={loading}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  id="messageInput"
                  label={contactMessage}
                  multiline
                  name="message"
                  inputRef={messageRef}
                  rows={7}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={dense ? 2 : 0}>
                  {showContactLocationSelector && (
                    <Grid item xs={dense ? 6 : 12}>
                      <CustomSelect
                        fullWidth
                        id="contactLocationInput"
                        label={i18n.translations.contactLocation}
                        name="contactLocation"
                        inputRef={contactLocationRef}
                        disabled={loading}
                        options={contactLocationsOptions}
                        defaultValue=""
                        onChange={handleLocationChange}
                      />
                    </Grid>
                  )}

                  {showTeamMemberSelector && (
                    <Grid item xs={dense ? 6 : 12}>
                      <CustomSelect
                        fullWidth
                        id="teamMemberInput"
                        label={i18n.translations.contactTeamMember}
                        name="teamMember"
                        inputRef={teamMemberRef}
                        disabled={loading}
                        options={teamMemberOptions}
                        defaultValue=""
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>


              {/* 
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      inputRef={newsletterRef}
                      icon={<CheckBoxOutlineBlankRoundedIcon style={{ fontSize: 15, color: "#fff" }} />}
                      checkedIcon={<CheckBoxRoundedIcon style={{ fontSize: 15, color: "#fff" }} />}
                    />
                  }
                  label={<Typography style={{ color: "#fff", fontSize: 12 }}>
                    Sunt de acord să primesc Newsletter din partea ARES
                  </Typography>}
                />
              </Grid>
              */}
            </Grid>

            <Typography
              sx={{
                color: theme => theme.palette.common.white,
                fontSize: 12,
                textAlign: "center",
                paddingTop: 1,
                paddingBottom: 1,
              }}
              dangerouslySetInnerHTML={{
                __html: disclaimerMessage,
              }}
            />

            <CustomButton
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                marginTop: 1,
                boxShadow: "none",
                borderRadius: 0,
                textTransform: "initial",
                fontSize: 14,
              }}
              disabled={loading}
            >
              {i18n.translations.sendMessage}
            </CustomButton>
          </form>
        </>
      )}
    </Box>
  )
}

export default GeneralContact

GeneralContact.propTypes = {
  formName: PropTypes.string,
  location: PropTypes.object.isRequired,
  locale: PropTypes.string,
  selectedHelperUid: PropTypes.string,
  selectedTeamMemberUid: PropTypes.string,
  selectedContactLocationUid: PropTypes.string,
  spread: PropTypes.bool,
  contactMessage: PropTypes.bool,
  disclaimerMessage: PropTypes.string,
  redirectTo: PropTypes.string,
  sendToCRM: PropTypes.bool,
}
